import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Star, MapPin, Users, BedDouble, Bath, Heart, Send, Loader2, CheckCircle2, BadgeCheck, MessageSquare, Clock, Globe, Calendar as CalendarIcon, ShieldCheck, Minus, Plus } from "lucide-react";
import MapView from "@/components/MapView";
import { AMENITY_CATEGORIES, AMENITY_LABELS, BADGES } from "@/lib/amenities";

const resolvePhoto = (p) => {
  if (!p) return "";
  if (p.startsWith("http")) return p;
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
};

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);

  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [pets, setPets] = useState(0);
  const guests = adults + children;  // infants don't count toward capacity
  const setGuests = (n) => setAdults(Math.max(1, Math.min(n, 30)));
  const [submitting, setSubmitting] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  // Reviews
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [subRatings, setSubRatings] = useState({
    cleanliness: 5,
    communication: 5,
    location: 5,
    value: 5,
    accuracy: 5,
  });

  // Wishlist picker
  const [picker, setPicker] = useState(null);

  // Messaging
  const [msg, setMsg] = useState("");

  // Photo lightbox
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = () => setLightboxIdx(null);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const total = (listing?.photos || []).length;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") setLightboxIdx((i) => (i === null ? 0 : (i + 1) % total));
      if (e.key === "ArrowLeft") setLightboxIdx((i) => (i === null ? 0 : (i - 1 + total) % total));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, listing]);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data)).finally(() => setLoading(false));
  }, [id]);

  const nights = (() => {
    if (!checkIn || !checkOut) return 0;
    const d1 = new Date(checkIn), d2 = new Date(checkOut);
    return Math.max(0, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
  })();

  // Fetch live pricing breakdown whenever dates/guests change
  useEffect(() => {
    if (!id || !checkIn || !checkOut || nights <= 0) {
      setPricing(null);
      return;
    }
    setPricingLoading(true);
    api.get(`/listings/${id}/pricing`, { params: { check_in: checkIn, check_out: checkOut, guests } })
      .then(({ data }) => setPricing(data))
      .catch(() => setPricing(null))
      .finally(() => setPricingLoading(false));
  }, [id, checkIn, checkOut, guests, nights]);

  const handleBook = async () => {
    if (!user) {
      toast.error("Connectez-vous pour réserver");
      navigate("/login");
      return;
    }
    if (!checkIn || !checkOut || nights <= 0) {
      toast.error("Sélectionnez des dates valides");
      return;
    }
    if (guests > listing.max_guests) {
      toast.error(`Maximum ${listing.max_guests} voyageurs`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        listing_id: id,
        check_in: checkIn,
        check_out: checkOut,
        guests,
        adults, children, infants, pets,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la réservation");
      setSubmitting(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return navigate("/login");
    try {
      await api.post("/reviews", {
        listing_id: id, rating, comment,
        rating_cleanliness: subRatings.cleanliness,
        rating_communication: subRatings.communication,
        rating_location: subRatings.location,
        rating_value: subRatings.value,
        rating_accuracy: subRatings.accuracy,
      });
      toast.success("Avis publié");
      setComment("");
      const { data } = await api.get(`/listings/${id}`);
      setListing(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!user) return navigate("/login");
    if (!msg.trim()) return;
    try {
      await api.post("/messages", {
        recipient_id: listing.owner_id,
        listing_id: id,
        content: msg,
      });
      toast.success("Message envoyé");
      setMsg("");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const toggleFav = async () => {
    if (!user) return navigate("/login");
    try {
      await api.post(`/favorites/${id}`);
      toast.success("Favori mis à jour");
    } catch (_) {}
  };

  if (loading) return <div className="pt-32 text-center text-neutral-500">Chargement…</div>;
  if (!listing) return <div className="pt-32 text-center">Annonce introuvable</div>;

  const photos = listing.photos || [];
  const main = resolvePhoto(photos[0]);
  const others = photos.slice(1, 5).map(resolvePhoto);

  return (
    <div className="pt-24 sm:pt-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
      {/* Photo Lightbox */}
      {lightboxIdx !== null && photos.length > 0 && (
        <PhotoLightbox
          photos={photos.map(resolvePhoto)}
          index={lightboxIdx}
          onClose={closeLightbox}
          onNav={(d) => setLightboxIdx((i) => ((i + d + photos.length) % photos.length))}
        />
      )}
      {/* Title block */}
      <div className="mb-8">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
            {listing.region}
          </div>
          {Array.isArray(listing.badges) && listing.badges.map((b) => BADGES[b] && (
            <span key={b} data-testid={`detail-badge-${b}`} className={`px-2 py-1 text-[9px] uppercase tracking-[0.18em] font-bold ${BADGES[b].className}`}>
              {BADGES[b].label}
            </span>
          ))}
        </div>
        <h1 data-testid="listing-title" className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          {listing.title}
        </h1>
        <div className="mt-4 flex items-center gap-6 text-sm flex-wrap">
          {listing.avg_rating && (
            <div className="flex items-center gap-1.5">
              <Star className="w-4 h-4 fill-[#0A0A0A] text-[#0A0A0A]" />
              <strong>{listing.avg_rating}</strong>
              <span className="text-neutral-500">· {listing.review_count} avis</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-neutral-700">
            <MapPin className="w-4 h-4" /> {listing.city}, {listing.region}
          </div>
          <button data-testid="listing-fav-btn" onClick={toggleFav} className="flex items-center gap-1.5 underline underline-offset-4 hover:text-[#1B2A49]">
            <Heart className="w-4 h-4" /> Enregistrer
          </button>
        </div>
      </div>

      {/* Gallery bento */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-12 rounded-lg overflow-hidden" data-testid="listing-gallery">
        <button
          type="button"
          onClick={() => photos.length && openLightbox(0)}
          disabled={!photos.length}
          data-testid="gallery-main"
          className="md:col-span-8 aspect-[4/3] md:aspect-auto md:min-h-[420px] bg-[#F5F2EC] overflow-hidden block cursor-zoom-in disabled:cursor-default group"
        >
          {main ? (
            <img src={main} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-neutral-500 bg-gradient-to-br from-[#F5F2EC] to-[#E5E1D8]">
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold mb-2">Photos à venir</div>
              <p className="text-xs italic text-neutral-400 max-w-xs text-center px-6">L'hôte met en ligne ses photos prochainement.</p>
            </div>
          )}
        </button>
        <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-2 relative">
          {others.length === 0 ? (
            <>
              <div className="aspect-square md:aspect-auto md:min-h-[210px] bg-[#F5F2EC]" />
              <div className="aspect-square md:aspect-auto md:min-h-[206px] bg-[#E5E1D8]" />
            </>
          ) : (
            others.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => openLightbox(i + 1)}
                data-testid={`gallery-thumb-${i}`}
                className="aspect-square md:aspect-auto md:min-h-[206px] bg-neutral-100 overflow-hidden cursor-zoom-in group"
              >
                <img src={p} alt="" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
              </button>
            ))
          )}
          {photos.length > 5 && (
            <button
              type="button"
              onClick={() => openLightbox(0)}
              data-testid="gallery-see-all"
              className="absolute bottom-2 right-2 bg-white text-[#0A0A0A] text-xs font-bold px-3 py-2 shadow hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              Voir les {photos.length} photos
            </button>
          )}
        </div>
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="border-b border-black/10 pb-8 mb-8">
            <div className="prose prose-sm max-w-none text-neutral-800 leading-relaxed whitespace-pre-line">
              {(listing.description || "").split("\n").map((line, i) => {
                if (line.startsWith("### ")) {
                  return <h4 key={i} className="font-display text-lg font-bold mt-5 mb-2">{line.replace(/^###\s*/, "")}</h4>;
                }
                if (line.startsWith("## ")) {
                  return <h3 key={i} className="font-display text-xl font-bold mt-6 mb-3">{line.replace(/^##\s*/, "")}</h3>;
                }
                return <p key={i} className="mb-2">{line}</p>;
              })}
            </div>
          </div>

          {listing.amenities?.length > 0 && (
            <div className="border-b border-black/10 pb-8 mb-8">
              <div className="font-display text-2xl font-bold mb-6">Équipements</div>
              <div className="space-y-6">
                {AMENITY_CATEGORIES.map((cat) => {
                  const items = cat.items.filter((it) => listing.amenities.includes(it.key));
                  if (!items.length) return null;
                  const Icon = cat.icon;
                  return (
                    <div key={cat.key}>
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className="w-4 h-4" />
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold">{cat.label}</div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 text-sm pl-6">
                        {items.map((it) => (
                          <div key={it.key} className="flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-[#0A0A0A]" />
                            <span>{it.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {/* Legacy free-text amenities not matching catalog keys */}
                {(() => {
                  const known = new Set(AMENITY_CATEGORIES.flatMap((c) => c.items.map((i) => i.key)));
                  const legacy = listing.amenities.filter((a) => !known.has(a));
                  if (!legacy.length) return null;
                  return (
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.25em] font-bold mb-3">Autres</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 text-sm pl-6">
                        {legacy.map((a) => (
                          <div key={a} className="flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-[#0A0A0A]" />
                            <span>{a}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Beds (couchage) */}
          {Array.isArray(listing.beds) && listing.beds.length > 0 && (
            <div className="border-b border-black/10 pb-8 mb-8" data-testid="beds-section">
              <div className="font-display text-2xl font-bold mb-4">Couchage</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {listing.beds.map((b, i) => (
                  <div key={i} className="flex items-center gap-3 border border-black/10 p-3" data-testid={`bed-row-${i}`}>
                    <div className="w-10 h-10 bg-[#F5F2EC] flex items-center justify-center">
                      <BedDouble className="w-5 h-5 text-[#0A0A0A]" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{b.room || `Chambre ${i + 1}`}</div>
                      <div className="text-xs text-neutral-600">Lit {b.size || "—"} cm</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* House rules */}
          {listing.rules && (
            <div className="border-b border-black/10 pb-8 mb-8" data-testid="rules-section">
              <div className="font-display text-2xl font-bold mb-4">Règles du logement</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm">
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-neutral-500" /> Arrivée à partir de <strong>{listing.rules.check_in_time || "—"}</strong></div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-neutral-500" /> Départ avant <strong>{listing.rules.check_out_time || "—"}</strong></div>
                {listing.rules.self_check_in && <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#0A0A0A]" /> Arrivée autonome</div>}
                {listing.rules.no_smoking && <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#0A0A0A]" /> Non fumeur</div>}
                {listing.rules.no_parties && <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#0A0A0A]" /> Fêtes interdites</div>}
                {listing.rules.quiet_hours && <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#0A0A0A]" /> Silence après 22h</div>}
                <div className="flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${listing.pets_allowed || listing.rules.pets_allowed ? "text-[#0A0A0A]" : "text-neutral-300"}`} />
                  Animaux {listing.pets_allowed || listing.rules.pets_allowed ? "acceptés" : "non acceptés"}
                </div>
                {listing.rules.max_visitors > 0 && <div className="flex items-center gap-2"><Users className="w-4 h-4 text-neutral-500" /> Max {listing.rules.max_visitors} visiteurs</div>}
              </div>
              {listing.rules.cleaning_conditions && (
                <div className="mt-4 p-3 bg-[#FAFAFA] border border-black/10 text-sm">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1 text-neutral-500">Conditions de ménage</div>
                  {listing.rules.cleaning_conditions}
                </div>
              )}
            </div>
          )}

          {/* Cancellation policy */}
          {listing.cancellation_policy && (
            <div className="border-b border-black/10 pb-8 mb-8" data-testid="cancellation-section">
              <div className="font-display text-2xl font-bold mb-3">Conditions d'annulation</div>
              <div className="flex items-start gap-3 text-sm">
                <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold capitalize">
                    {{ flexible: "Flexible", moderate: "Modérée", strict: "Stricte" }[listing.cancellation_policy] || listing.cancellation_policy}
                  </div>
                  <p className="text-neutral-600 mt-1">
                    {{
                      flexible: "Annulation gratuite jusqu'à 24h avant l'arrivée. Au-delà, la première nuit reste due.",
                      moderate: "Annulation gratuite jusqu'à 5 jours avant l'arrivée. Au-delà, 50% du séjour reste dû.",
                      strict: "Annulation gratuite uniquement dans les 48h suivant la réservation, et au moins 14 jours avant l'arrivée.",
                    }[listing.cancellation_policy] || ""}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          <div className="border-b border-black/10 pb-8 mb-8">
            <div className="font-display text-2xl font-bold mb-4">Où se trouve le logement</div>
            <div className="flex items-start gap-3 mb-5">
              <MapPin className="w-5 h-5 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">{listing.city}, {listing.region}</div>
                {listing.address && (
                  <div className="text-sm text-neutral-600 mt-1">
                    {user ? listing.address : <em>L'adresse précise est communiquée après confirmation de la réservation.</em>}
                  </div>
                )}
              </div>
            </div>
            {typeof listing.lat === "number" && typeof listing.lng === "number" && (
              <MapView
                listings={[{ ...listing, photos: [] }]}
                height={320}
              />
            )}
          </div>

          {/* Host */}
          {listing.owner && (() => {
            const hp = listing.owner.host_profile || {};
            const stats = listing.owner.stats || {};
            const ownerAvatar = hp.avatar || listing.owner.avatar;
            const photoSrc = ownerAvatar ? (ownerAvatar.startsWith("http") ? ownerAvatar : `${process.env.REACT_APP_BACKEND_URL}/api/files/${ownerAvatar}`) : null;
            const displayName = hp.first_name || listing.owner.name?.split(" ")[0] || "Hôte";
            const langLabels = { fr: "Français", en: "English", br: "Brezhoneg", es: "Español", de: "Deutsch", it: "Italiano", nl: "Nederlands", pt: "Português" };
            const hostTypeLabels = { particulier: "Particulier", conciergerie: "Conciergerie", professionnel: "Professionnel" };
            return (
              <div className="border-b border-black/10 pb-8 mb-8" data-testid="host-card">
                <div className="font-display text-2xl font-bold mb-5">Votre hôte</div>
                <div className="flex items-start gap-5">
                  <div className="w-20 h-20 rounded-full bg-[#0A0A0A] text-white overflow-hidden shrink-0 flex items-center justify-center font-bold text-2xl font-display">
                    {photoSrc ? <img src={photoSrc} alt="" className="w-full h-full object-cover" /> : displayName[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-xl font-bold">{displayName}</div>
                      {stats.superhost && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-[#7A1535] text-white text-[9px] uppercase tracking-[0.18em] font-bold">
                          <BadgeCheck className="w-3 h-3" /> Super hôte
                        </span>
                      )}
                      {listing.owner.email_verified && (
                        <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#166534] font-bold">
                          <CheckCircle2 className="w-3 h-3" /> Email vérifié
                        </span>
                      )}
                      {listing.owner.phone_verified && (
                        <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#166534] font-bold">
                          <CheckCircle2 className="w-3 h-3" /> Téléphone vérifié
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-neutral-600 mt-1">
                      {hp.host_type && hostTypeLabels[hp.host_type]}
                      {hp.hosting_since && ` · Hôte depuis ${hp.hosting_since}`}
                      {hp.age && ` · ${hp.age} ans`}
                    </div>
                    {hp.description && (
                      <p className="text-sm text-neutral-700 mt-3 leading-relaxed whitespace-pre-line">{hp.description}</p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 text-sm">
                      {stats.review_count > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Avis</div>
                          <div className="font-display text-lg font-bold mt-0.5">{stats.review_count}</div>
                        </div>
                      )}
                      {stats.avg_rating && (
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Note moy.</div>
                          <div className="font-display text-lg font-bold mt-0.5 flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 fill-[#0A0A0A] text-[#0A0A0A]" /> {stats.avg_rating}
                          </div>
                        </div>
                      )}
                      {stats.response_rate != null && (
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Taux de réponse</div>
                          <div className="font-display text-lg font-bold mt-0.5">{stats.response_rate}%</div>
                        </div>
                      )}
                      {stats.response_time && (
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Temps de réponse</div>
                          <div className="font-display text-lg font-bold mt-0.5">{stats.response_time}</div>
                        </div>
                      )}
                    </div>
                    {hp.languages?.length > 0 && (
                      <div className="mt-4 flex items-center gap-2 text-xs text-neutral-600">
                        <Globe className="w-3.5 h-3.5" />
                        {hp.languages.map((l) => langLabels[l] || l).join(" · ")}
                      </div>
                    )}
                  </div>
                </div>
                {user && user.id !== listing.owner_id && (
                  <form onSubmit={sendMessage} className="mt-6 flex gap-2">
                    <input
                      data-testid="contact-host-input"
                      value={msg}
                      onChange={(e) => setMsg(e.target.value)}
                      placeholder={`Écrire un message à ${displayName}…`}
                      className="flex-1 border-b-2 border-black/20 focus:border-[#0A0A0A] outline-none py-2 text-sm bg-transparent"
                    />
                    <button data-testid="contact-host-btn" type="submit" className="bg-[#0A0A0A] text-white px-5 py-2 text-sm font-semibold hover:bg-[#1B2A49] transition-colors flex items-center gap-2">
                      <Send className="w-4 h-4" /> Envoyer
                    </button>
                  </form>
                )}
              </div>
            );
          })()}

          {/* Reviews */}
          <div>
            <div className="font-display text-2xl font-bold mb-6">
              Avis {listing.review_count > 0 && <span className="text-neutral-500">({listing.review_count})</span>}
            </div>

            {/* Rating breakdown */}
            {(() => {
              const revs = listing.reviews || [];
              const subKeys = [
                { k: "rating_cleanliness", l: "Propreté" },
                { k: "rating_communication", l: "Communication" },
                { k: "rating_location", l: "Emplacement" },
                { k: "rating_value", l: "Rapport qualité-prix" },
                { k: "rating_accuracy", l: "Précision de l'annonce" },
              ];
              const avgs = subKeys.map((s) => {
                const vals = revs.map((r) => r[s.k]).filter((v) => v != null);
                if (!vals.length) return null;
                return { ...s, avg: vals.reduce((a, b) => a + b, 0) / vals.length };
              }).filter(Boolean);
              if (avgs.length === 0) return null;
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3 mb-8 pb-8 border-b border-black/10" data-testid="rating-breakdown">
                  {avgs.map((a) => (
                    <div key={a.k} className="flex items-center gap-3" data-testid={`rb-${a.k}`}>
                      <span className="text-sm font-semibold w-44 shrink-0">{a.l}</span>
                      <div className="flex-1 h-1 bg-neutral-200 rounded-full overflow-hidden">
                        <div className="h-full bg-[#0A0A0A]" style={{ width: `${(a.avg / 5) * 100}%` }} />
                      </div>
                      <span className="text-sm font-bold tabular-nums w-8 text-right">{a.avg.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}

            <div className="space-y-6">
              {(listing.reviews || []).map((r) => (
                <div key={r.id} className="border-b border-black/10 pb-6">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold">{r.user_name}</div>
                      {r.imported && r.source === "airbnb" && (
                        <span className="text-[9px] uppercase tracking-[0.18em] font-bold bg-[#FF5A5F] text-white px-2 py-0.5" title="Avis migré depuis Airbnb par le propriétaire">
                          Avis Airbnb vérifié
                        </span>
                      )}
                      {r.review_date && (
                        <span className="text-xs text-neutral-500">· {r.review_date}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="w-3.5 h-3.5 fill-[#0A0A0A] text-[#0A0A0A]" /> {r.rating}
                    </div>
                  </div>
                  <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{r.comment}</p>
                </div>
              ))}
              {listing.review_count === 0 && (
                <p className="text-sm text-neutral-500 italic">Aucun avis pour le moment.</p>
              )}
            </div>

            {user && user.id !== listing.owner_id && (
              <form onSubmit={submitReview} className="mt-8 p-6 border border-black/10 bg-white" data-testid="review-form">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 mb-3">Laisser un avis</div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500 w-44">Note globale</span>
                  {[1,2,3,4,5].map((n) => (
                    <button data-testid={`star-${n}`} type="button" key={n} onClick={() => setRating(n)}>
                      <Star className={`w-6 h-6 ${n <= rating ? "fill-[#0A0A0A] text-[#0A0A0A]" : "text-neutral-300"}`} />
                    </button>
                  ))}
                </div>
                {/* Sub-ratings */}
                <div className="space-y-2 mb-4">
                  {[
                    { k: "cleanliness", l: "Propreté" },
                    { k: "communication", l: "Communication" },
                    { k: "location", l: "Emplacement" },
                    { k: "value", l: "Rapport qualité-prix" },
                    { k: "accuracy", l: "Précision de l'annonce" },
                  ].map(({ k, l }) => (
                    <div key={k} className="flex items-center gap-2" data-testid={`sub-${k}`}>
                      <span className="text-xs text-neutral-700 w-44">{l}</span>
                      {[1,2,3,4,5].map((n) => (
                        <button type="button" key={n} onClick={() => setSubRatings((s) => ({ ...s, [k]: n }))}>
                          <Star className={`w-4 h-4 ${n <= subRatings[k] ? "fill-[#0A0A0A] text-[#0A0A0A]" : "text-neutral-300"}`} />
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
                <textarea
                  data-testid="review-text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  placeholder="Votre expérience…"
                  className="w-full border border-black/15 p-3 text-sm outline-none focus:border-[#0A0A0A]"
                />
                <button data-testid="review-submit-btn" type="submit" className="mt-3 bg-[#0A0A0A] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1B2A49] transition-colors">
                  Publier l'avis
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Booking widget */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 border border-black/15 bg-white p-6 shadow-xl">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <span className="font-display text-3xl font-bold">{listing.price_per_night}€</span>
                <span className="text-sm text-neutral-500"> / nuit</span>
              </div>
              {listing.avg_rating && (
                <div className="flex items-center gap-1 text-sm font-semibold">
                  <Star className="w-3.5 h-3.5 fill-[#0A0A0A] text-[#0A0A0A]" /> {listing.avg_rating}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-0 border border-black/15 mb-3">
              <label className="p-3 border-r border-black/15">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Arrivée</div>
                <input data-testid="bk-checkin" type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className="w-full bg-transparent outline-none text-sm font-semibold mt-1" />
              </label>
              <label className="p-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Départ</div>
                <input data-testid="bk-checkout" type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className="w-full bg-transparent outline-none text-sm font-semibold mt-1" />
              </label>
            </div>
            <details className="block border border-black/15 mb-4" data-testid="bk-guests-details">
              <summary className="p-3 cursor-pointer list-none flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Voyageurs</div>
                  <div className="text-sm font-semibold mt-1">
                    {guests} voyageur{guests > 1 ? "s" : ""}
                    {infants > 0 && ` · ${infants} bébé${infants > 1 ? "s" : ""}`}
                    {pets > 0 && ` · ${pets} animal${pets > 1 ? "ux" : ""}`}
                  </div>
                </div>
                <span className="text-xs">▾</span>
              </summary>
              <div className="border-t border-black/10 p-3 space-y-3">
                {[
                  { k: "adults", label: "Adultes", sub: "13 ans et +", val: adults, set: setAdults, min: 1 },
                  { k: "children", label: "Enfants", sub: "2 – 12 ans", val: children, set: setChildren, min: 0 },
                  { k: "infants", label: "Bébés", sub: "moins de 2 ans", val: infants, set: setInfants, min: 0 },
                  ...(listing.pets_allowed ? [{ k: "pets", label: "Animaux", sub: "acceptés par l'hôte", val: pets, set: setPets, min: 0 }] : []),
                ].map((r) => (
                  <div key={r.k} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold">{r.label}</div>
                      <div className="text-[11px] text-neutral-500">{r.sub}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid={`bk-${r.k}-dec`}
                        onClick={() => r.set(Math.max(r.min, r.val - 1))}
                        disabled={r.val <= r.min}
                        className="w-7 h-7 border border-black/20 flex items-center justify-center disabled:opacity-30"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span data-testid={`bk-${r.k}-val`} className="w-6 text-center text-sm font-semibold">{r.val}</span>
                      <button
                        type="button"
                        data-testid={`bk-${r.k}-inc`}
                        onClick={() => {
                          if (r.k === "adults" || r.k === "children") {
                            if (guests + 1 > listing.max_guests) { toast.error(`Maximum ${listing.max_guests} voyageurs`); return; }
                          }
                          r.set(r.val + 1);
                        }}
                        className="w-7 h-7 border border-black/20 flex items-center justify-center"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
                {!listing.pets_allowed && (
                  <p className="text-[11px] text-neutral-500 italic">L'hôte n'accepte pas les animaux.</p>
                )}
                <p className="text-[11px] text-neutral-500">Capacité maximale : {listing.max_guests} voyageurs (hors bébés).</p>
              </div>
            </details>

            <button
              data-testid="book-stripe-btn"
              onClick={handleBook}
              disabled={submitting}
              className="w-full bg-[#0A0A0A] text-white py-4 font-semibold hover:bg-[#1B2A49] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirection…</> : "Réserver avec Stripe"}
            </button>

            {nights > 0 && pricing && (
              <div className="mt-6 space-y-2 text-sm" data-testid="pricing-breakdown">
                <div className="flex justify-between">
                  <span className="underline underline-offset-4 decoration-dotted">
                    {listing.price_per_night}€ × {pricing.nights} nuits
                  </span>
                  <span>{pricing.subtotal.toFixed(2)}€</span>
                </div>
                {pricing.discount_amount > 0 && (
                  <div className="flex justify-between text-[#166534]">
                    <span>Réduction long séjour (−{pricing.discount_percent}%)</span>
                    <span>−{pricing.discount_amount.toFixed(2)}€</span>
                  </div>
                )}
                {pricing.cleaning_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="underline underline-offset-4 decoration-dotted" title="Forfait facturé une seule fois — nettoyage professionnel à votre départ. Non remboursable.">
                      Forfait ménage
                    </span>
                    <span>{pricing.cleaning_fee.toFixed(2)}€</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="underline underline-offset-4 decoration-dotted" title="10% sur l'hébergement, hors frais de ménage">
                    Frais de service (10%)
                  </span>
                  <span>{pricing.platform_fee.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between">
                  <span className="underline underline-offset-4 decoration-dotted" title={`Taxe de séjour à ${pricing.city} · ${pricing.tourist_tax_per_person_per_night}€/pers/nuit · reversée par l'hôte à la commune`}>
                    Taxe de séjour ({pricing.city})
                  </span>
                  <span>{pricing.tourist_tax.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between border-t border-black/10 pt-3 font-bold text-base">
                  <span>Total</span>
                  <span>{pricing.total.toFixed(2)}€</span>
                </div>
                {pricing.security_deposit > 0 && (
                  <div className="mt-3 p-3 bg-[#F5F2EC] text-xs leading-relaxed">
                    <strong>Caution de {pricing.security_deposit.toFixed(0)}€</strong> non débitée — à régler à l'arrivée auprès du propriétaire.
                  </div>
                )}
                {pricing.cleaning_fee > 0 && (
                  <div className="mt-2 p-3 bg-white border border-black/10 text-xs leading-relaxed">
                    <div className="flex items-baseline justify-between mb-1">
                      <strong className="text-[11px] uppercase tracking-[0.15em]">Détail du forfait ménage</strong>
                      <span className="font-bold">{pricing.cleaning_fee.toFixed(2)}€</span>
                    </div>
                    <p className="text-neutral-600">
                      Nettoyage professionnel du logement effectué à votre départ. Facturé une seule fois pour l'ensemble du séjour, indépendamment du nombre de nuits. Non remboursable.
                    </p>
                  </div>
                )}
                <p className="text-[10px] text-neutral-500 mt-2 leading-relaxed">
                  La taxe de séjour est collectée par l'hôte et reversée à la commune de {pricing.city}.
                </p>
              </div>
            )}
            {pricingLoading && nights > 0 && (
              <div className="mt-6 text-xs text-neutral-500 text-center">Calcul du total…</div>
            )}

            <p className="mt-4 text-xs text-neutral-500 text-center">Aucune commission cachée · Paiement sécurisé Stripe</p>

            {/* Paiement protégé badge */}
            <div className="mt-4 p-3 bg-[#F5F2EC] border border-black/10 flex items-start gap-3" data-testid="paiement-protege-badge">
              <div className="w-9 h-9 bg-[#635BFF] text-white flex items-center justify-center shrink-0 rounded-sm">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="flex-1 text-xs leading-relaxed">
                <div className="font-bold text-[11px] uppercase tracking-[0.18em] mb-0.5">Paiement protégé</div>
                <p className="text-neutral-700">
                  Votre paiement est protégé par <strong>Stripe</strong>. L'hôte est vérifié et son compte bancaire est validé. Aucune information bancaire ne transite par MSB.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ====================== Photo Lightbox ====================== */
function PhotoLightbox({ photos, index, onClose, onNav }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 text-white text-3xl w-12 h-12 flex items-center justify-center hover:bg-white/10 z-10"
        aria-label="Fermer"
        data-testid="lb-close"
      >
        ×
      </button>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 text-white text-3xl w-14 h-14 flex items-center justify-center hover:bg-white/10 z-10"
        aria-label="Précédent"
        data-testid="lb-prev"
      >
        ‹
      </button>

      <div className="absolute top-4 left-4 text-white/80 text-sm font-bold tracking-[0.15em]" data-testid="lb-counter">
        {index + 1} / {photos.length}
      </div>

      <img
        src={photos[index]}
        alt={`photo ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] object-contain shadow-2xl"
        data-testid="lb-image"
      />

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 text-white text-3xl w-14 h-14 flex items-center justify-center hover:bg-white/10 z-10"
        aria-label="Suivant"
        data-testid="lb-next"
      >
        ›
      </button>

      {/* Thumbnails strip */}
      {photos.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-4">
          {photos.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => { e.stopPropagation(); onNav(i - index); }}
              className={`shrink-0 w-16 h-16 overflow-hidden border-2 ${i === index ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`}
              data-testid={`lb-thumb-${i}`}
            >
              <img src={p} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
